import { Alert, Typography } from '@mui/material';

/**
 * Geokodowanie na ekranie (06.10.2026; API: `apps/api/src/geocoding/`).
 *
 * - miejscowość przy pozycji z GPS („GPS 02.10, 08:22 · okolice: Poznań
 *   (PL)”) — w kolumnie „Wolny od” obu kierunków rekomendacji;
 * - punkt zlecenia wyznaczony geokodowaniem kodu pocztowego albo
 *   miejscowości — dokładność pokazana przy dolocie, bo odległość liczy się
 *   do środka obszaru, nie do adresu;
 * - na karcie zlecenia: skąd punkt na mapie i czemu go nie ma.
 *
 * Wszystkie pola są opcjonalne: starsze API i wyłączone geokodowanie ich
 * nie mają — wtedy ekran wygląda jak dotąd.
 */

/** Miejscowość przy pozycji GPS (API: `PositionPlace`). */
export interface PositionPlace {
  city: string;
  country: string | null;
  source?: string;
}

/** Punkt zlecenia z geokodowania (API: `OrderPointGeo`). */
export interface OrderPointGeo {
  source: string;
  precision: string;
  /** Zdanie dla planisty, gotowe z API. */
  text: string;
}

/** „okolice: Poznań (PL)” — nazwy miejscowości nie odmieniamy. */
export function placeText(place: PositionPlace | null | undefined): string | null {
  if (!place?.city) return null;
  return `okolice: ${place.city}${place.country ? ` (${place.country})` : ''}`;
}

/**
 * Punkt załadunku albo rozładunku zlecenia wyznaczony geokodowaniem — pod
 * nagłówkiem rekomendacji pojazdów. Nic, gdy punkty są z TMS.
 */
export function PickupGeoNote({
  order,
}: {
  order?: { pickupGeo?: OrderPointGeo | null; deliveryGeo?: OrderPointGeo | null } | null;
}) {
  const notes = [order?.pickupGeo, order?.deliveryGeo].filter(
    (g): g is OrderPointGeo => !!g?.text,
  );
  if (notes.length === 0) return null;
  return (
    <Alert severity="info" sx={{ mb: 1 }} data-testid="pickup-geo-note">
      {notes.map((g) => (
        <Typography key={g.text} variant="body2">
          {g.text}
        </Typography>
      ))}
    </Alert>
  );
}

/**
 * Nazwa źródła punktu. GeoNames (zapasowy słownik kodów, 19.10.2026) —
 * z licencją: CC BY 4.0 wymaga podania źródła przy danych.
 */
const SOURCE_LABELS: Record<string, string> = {
  here: 'HERE',
  tomtom: 'TomTom',
  geonames: 'słownik GeoNames, CC BY 4.0',
};
const PRECISION_LABELS: Record<string, string> = {
  'postal-code': 'kod pocztowy',
  locality: 'miejscowość',
};

/** Skąd punkt jednego końca — słowami. */
export function endPointText(
  lat: unknown,
  lng: unknown,
  source: unknown,
  precision: unknown,
): string {
  const hasPoint = typeof lat === 'number' && typeof lng === 'number';
  if (!hasPoint) return 'brak';
  if (typeof source !== 'string' || source === '') return 'ze źródła zlecenia';
  const how = PRECISION_LABELS[String(precision)] ?? String(precision ?? '');
  return `z geokodowania: ${how} (${SOURCE_LABELS[source] ?? source})`;
}

/**
 * Wiersz „Punkt na mapie” na karcie zlecenia: skąd punkt załadunku
 * i rozładunku, a gdy go nie ma — powód z geokodowania (np. „brak kraju
 * punktu”). Bez punktu rekomendacje pojazdów odmawiają z powodem.
 */
export function GeoPointsInfo({ record }: { record: Record<string, unknown> }) {
  const from = endPointText(
    record.fromLat,
    record.fromLng,
    record.fromGeoSource,
    record.fromGeoPrecision,
  );
  const to = endPointText(record.toLat, record.toLng, record.toGeoSource, record.toGeoPrecision);
  const failure = typeof record.geocodingFailure === 'string' ? record.geocodingFailure : null;
  return (
    <>
      <Typography variant="body2" data-testid="geo-points">
        załadunek: {from} · rozładunek: {to}
      </Typography>
      {failure && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} data-testid="geo-failure">
          Nie wyznaczono punktu — {failure}
        </Typography>
      )}
    </>
  );
}
